/**
 * Compact label for name/value metafield/custom-field values.
 * Filtering still uses the full raw value.
 *
 * Parallel implementation lives in papaya/src/api/models/Unified/nameValueDisplay.ts
 * (`formatNameValueTitle`) — keep the rules in sync.
 */

export const NAME_VALUE_TITLE_MAX = 80

const NAME_VALUE_JSON_SUMMARY_KEYS = [
  'widgetType', 'slotName', 'type', 'name', 'title', 'status', 'key', 'location',
]

const isPlainObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const truncate = (value, maxLen) => {
  if (value.length <= maxLen) return value
  if (maxLen <= 1) return '…'
  return `${value.slice(0, maxLen - 1)}…`
}

const formatScalar = (value) => {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value) ?? ''
  } catch (_) {
    return String(value)
  }
}

/** Known Shopify metafield object shapes → short readable labels. */
const formatKnownObject = (obj) => {
  if ('value' in obj && 'unit' in obj && typeof obj.unit === 'string') {
    const value = formatScalar(obj.value)
    if (value) return `${value} ${obj.unit}`
  }
  if ('amount' in obj && 'currency_code' in obj && typeof obj.currency_code === 'string') {
    const amount = formatScalar(obj.amount)
    if (amount) return `${amount} ${obj.currency_code}`
  }
  if ('value' in obj && 'scale_max' in obj) {
    const value = formatScalar(obj.value)
    const scaleMax = formatScalar(obj.scale_max)
    if (value && scaleMax) return `${value} / ${scaleMax}`
  }
  return null
}

const formatObject = (obj) => {
  const known = formatKnownObject(obj)
  if (known) return known

  for (const key of NAME_VALUE_JSON_SUMMARY_KEYS) {
    const candidate = obj[key]
    if (candidate != null && typeof candidate !== 'object') {
      const str = String(candidate)
      if (str.length && str.length <= 60) {
        return `${key}: ${str}`
      }
    }
  }

  const pairs = []
  for (const [key, value] of Object.entries(obj)) {
    if (value == null) continue
    if (typeof value === 'object') continue
    pairs.push(`${key}: ${formatScalar(value)}`)
  }
  if (!pairs.length) return '(empty)'
  return pairs.join(', ')
}

const formatArrayItem = (item) => {
  if (isPlainObject(item)) return formatObject(item)
  return formatScalar(item)
}

/**
 * Fit whole items into maxLen, then append "+N more" for the remainder.
 * Always includes at least the first item (truncated if needed).
 */
const joinWithOverflow = (items, maxLen) => {
  if (!items.length) return '(empty)'
  if (items.length === 1) return truncate(items[0], maxLen)

  const moreSuffix = (n) => ` +${n} more`
  let included = 0
  let current = ''

  for (let i = 0; i < items.length; i++) {
    const next = included === 0 ? items[i] : `${current}, ${items[i]}`
    const remaining = items.length - (i + 1)
    const withSuffix = remaining > 0 ? `${next}${moreSuffix(remaining)}` : next
    if (withSuffix.length <= maxLen) {
      current = next
      included = i + 1
      continue
    }
    break
  }

  if (included === 0) {
    const remaining = items.length - 1
    const suffix = remaining > 0 ? moreSuffix(remaining) : ''
    const budget = Math.max(1, maxLen - suffix.length)
    return `${truncate(items[0], budget)}${suffix}`
  }

  const remaining = items.length - included
  return remaining > 0 ? `${current}${moreSuffix(remaining)}` : current
}

const summarizeParsed = (parsed, maxLen) => {
  if (Array.isArray(parsed)) {
    if (!parsed.length) return '(empty)'
    return joinWithOverflow(parsed.map(formatArrayItem).filter(Boolean), maxLen)
  }
  if (isPlainObject(parsed)) {
    if (!Object.keys(parsed).length) return '(empty)'
    return truncate(formatObject(parsed), maxLen)
  }
  return truncate(formatScalar(parsed), maxLen)
}

export const formatNameValueDisplay = (value, maxLen = NAME_VALUE_TITLE_MAX) => {
  if (value == null) return value
  if (typeof value === 'object') {
    try {
      return summarizeParsed(value, maxLen)
    } catch (_) {
      // fall through
    }
  }
  const trimmed = String(value).trim()
  if (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    try {
      return summarizeParsed(JSON.parse(trimmed), maxLen)
    } catch (_) {
      // fall through to truncate
    }
  }
  return truncate(trimmed, maxLen)
}
